import { Link } from '@tanstack/react-router';
import { Bell } from 'lucide-react';

import { t } from '../../../lib/i18n';
import { type NewBidEvent } from '../new-bid-events';
import { formatChf } from '../utils';

interface NewBidAlertProps {
  /** The last few captured bids from the shell's single `useAdminBidStream`. */
  events: NewBidEvent[];
  /** Stream state — the pill dims while the stream is down so a stale count is not read as live. */
  connected: boolean;
}

/**
 * Live "new bid landed" alert for the relay desk — the monorepo replacement
 * for the web's `AdminNewBidNotification`.
 *
 * Mounted once in the app shell so it fires wherever the operator is. Clicking
 * through goes to the relay desk, which is where a freshly captured bid has to
 * be acted on before its tender closes. The per-bid toast (with its «Відкрити»
 * action) is the immediate channel; this pill is the "last 5" summary that
 * outlives it.
 */
export function NewBidAlert({ events, connected }: NewBidAlertProps) {
  if (events.length === 0) return null;

  const count = events.length;
  const labels = events
    .map((event) => event.carLabel)
    .filter((label): label is string => Boolean(label));

  return (
    <Link
      to="/deals"
      search={{ tab: 'relay' }}
      className={`fixed right-4 top-4 z-50 flex items-center gap-2 rounded-full px-4 py-2 text-white shadow-lg transition-colors ${
        connected ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600/70 hover:bg-blue-700/80'
      }`}
      role="status"
      aria-live="polite"
    >
      <Bell size={16} aria-hidden />
      <span className="text-sm">
        <strong>{count}</strong>{' '}
        {t(count > 1 ? 'notifications.newBids.plural' : 'notifications.newBids.single')}
        {labels.length > 0 && (
          <>
            : <strong>{labels.join(', ')}</strong>
          </>
        )}{' '}
        <span className="opacity-80">
          ({formatChf(events[events.length - 1]?.bidAmount ?? 0)})
        </span>
      </span>
    </Link>
  );
}
